import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import TerrainViewer from "./TerrainViewer";
import "./TerritoryStage.css";

/**
 * L'ÉCRAN DE TERRITOIRE : une carte 3D qui appartient à la SÉANCE, pas à une page.
 *
 * POURQUOI CE MODULE EXISTE. La carte vivait dans `TerritoryIntroPage`, donc
 * dans une page du jeu — et `GamePage` démonte le composant de page à chaque
 * transition. Or reconstruire la scène ne coûte pas un aller-retour réseau (le
 * cache HTTP y suffirait) mais un `JSON.parse` de plusieurs mégaoctets, la
 * construction des géométries et la création d'un contexte WebGL : une à trois
 * secondes d'écran noir. Tant que la carte est propriété d'une page, elle ne
 * peut pas accompagner l'atelier au-delà de son module d'intro.
 *
 * CE QUE FAIT CE MODULE. Il monte UNE instance de `TerrainViewer`, une fois,
 * pour toute la partie. Les pages ne rendent plus la carte : elles DÉCLARENT ce
 * qu'elles en veulent (`useStageView`), et l'écran applique. Une page qui s'en
 * va relâche l'écran ; la scène, elle, reste construite.
 *
 * CE QU'IL NE FAIT PAS. Il ne touche pas à `TerrainViewer`, qui savait déjà
 * changer d'étape sans se reconstruire : son effet de construction est calé sur
 * `[territoryId, availableLayersKey]` et tout le reste passe par des effets
 * légers qui mutent la scène en place. Le problème n'a jamais été le viewer,
 * c'est qu'on le démontait.
 *
 * DEUX PROPRIÉTAIRES POSSIBLES. Le provider n'appartient pas au jeu : il
 * appartient à qui détient une durée de vie de carte. En partie, c'est la
 * séance (`GamePage`) ; dans l'aperçu du builder, c'est la modale — la scène
 * s'y construit à l'ouverture et se dispose à la fermeture, ce qui est bien le
 * comportement voulu là-bas. Le canevas d'authoring (`builder/StepCanvas`)
 * garde, lui, son propre viewer : composer une vue et jouer un atelier sont
 * deux mondes séparés.
 */

// Commandes — objet STABLE. Il ne doit jamais changer d'identité : les
// consommateurs le mettent dans les dépendances de leurs effets, et un nouvel
// objet à chaque `selection` les ferait tous se réabonner en boucle. D'où la
// séparation avec l'état réactif ci-dessous.
const TerritoryStageContext = createContext(null);

// État réactif — change souvent, et seuls ceux qui le lisent en pâtissent.
const TerritoryStageStateContext = createContext(null);

/** Commandes de l'écran. `null` hors provider. */
export function useTerritoryStage() {
  return useContext(TerritoryStageContext);
}

/** Ce que l'écran donne à voir en retour : sélection sur la carte, lisibilité. */
export function useTerritoryStageState() {
  return useContext(TerritoryStageStateContext) || EMPTY_STATE;
}

const EMPTY_STATE = { selection: null, uiScale: 1, hasStage: false };

/**
 * CE QU'UN MODE CHANGE DANS LE VIEWER.
 *
 * `stage`    — la carte EST la page : elle porte ses légendes, ses fiches, ses
 *              réglages, et se dessine à la fréquence de l'écran.
 * `located`  — la carte SITUE la page : la caméra vole jusqu'à un lieu, un
 *              marqueur s'y pose et sa fiche s'ouvre d'elle-même. Le marqueur
 *              et sa fiche sont le contenu ; les légendes et les réglages, qui
 *              servent à consulter une carte, n'ont rien à y faire.
 * `backdrop` — la carte est derrière une page de jeu : plus aucun panneau (une
 *              colonne de légendes par-dessus une mosaïque de décisions serait
 *              un contresens), plus aucun clic — donc plus aucun lancer de
 *              rayon, qui n'a lieu que sur les événements de pointeur — et un
 *              rendu bridé, puisque personne ne la manipule.
 * `off`      — personne ne la regarde : montée, chargée, gelée.
 */
const MODES = {
  stage: { chrome: "full", maxFps: null },
  located: { chrome: "markers", maxFps: null },
  backdrop: { chrome: "none", maxFps: 30 },
  off: { chrome: "none", maxFps: null },
};

/**
 * Déclare la vue du territoire pour la durée de vie de l'appelant.
 *
 * DEUX EFFETS, ET C'EST VOULU. La PROPRIÉTÉ de l'écran se prend au montage et
 * se rend au démontage ; la VUE se met à jour à chaque changement d'étape. Les
 * mêler relâcherait l'écran une fraction de commit à chaque étape — invisible
 * la plupart du temps, mais c'est le genre de scintillement qu'on ne retrouve
 * plus une fois qu'il apparaît sur un vidéoprojecteur.
 *
 * `view` à `null` = l'appelant ne revendique rien (module sans étape, page qui
 * n'utilise pas la carte) : l'écran reste à qui l'avait, ou éteint.
 */
export function useStageView(ownerId, view) {
  const stage = useTerritoryStage();
  const claims = Boolean(view && stage);

  useEffect(() => {
    if (!claims) return undefined;
    stage.claim(ownerId);
    return () => stage.release(ownerId);
  }, [stage, ownerId, claims]);

  // La vue est un objet littéral reconstruit à chaque rendu : c'est son
  // CONTENU qui doit décider d'une mise à jour, pas son identité.
  const viewKey = JSON.stringify(view ?? null);
  useEffect(() => {
    if (!claims) return;
    stage.setView(ownerId, view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, ownerId, claims, viewKey]);
}

/**
 * Ce que la scène doit AVOIR CONSTRUIT, réconcilié avec ce qu'une vue demande.
 *
 * L'ensemble des couches ne rétrécit JAMAIS. Changer `availableLayers`
 * reconstruit la scène (cf. l'effet de `TerrainViewer` calé sur
 * `availableLayersKey`) : si une page déclarait deux couches après une page qui
 * en déclarait cinq, on paierait une reconstruction complète pour AFFICHER
 * MOINS. Les couches se déclarent une fois, et chaque page allume ce qu'elle
 * montre via `active_layers`, ce qui ne coûte qu'une bascule de `.visible`.
 *
 * Une couche qui apparaît en cours de séance reconstruit donc la scène — c'est
 * l'échappatoire assumée, pas le régime normal. Le warning est là pour qu'elle
 * se voie en développement plutôt qu'en salle : la bonne réponse est de
 * déclarer l'union en tête d'atelier.
 */
function reconcileScene(current, view) {
  const territoryId = view?.territory_id || null;
  if (!territoryId) return current;

  const wanted = [...new Set(view.layers || [])].sort();
  if (!current || current.territoryId !== territoryId) {
    return { territoryId, layers: wanted };
  }

  const merged = [...new Set([...current.layers, ...wanted])].sort();
  // Identité conservée quand rien ne change : `setScene` ne provoque alors ni
  // rendu ni reconstruction.
  if (merged.length === current.layers.length) return current;

  const added = merged.filter((id) => !current.layers.includes(id));
  console.warn(
    `[TerritoryStage] couches ajoutées en cours de séance (${added.join(", ")}) : `
    + "la scène est reconstruite. Déclarez-les en tête d'atelier pour l'éviter."
  );
  return { territoryId, layers: merged };
}

/**
 * `hosted` : l'écran se cale sur son CONTENEUR (`position: absolute`) au lieu
 * de la fenêtre. L'appelant doit alors fournir une boîte positionnée aux mêmes
 * dimensions que la page — c'est ce que fait l'aperçu du builder, où un écran
 * en `fixed` recouvrirait la barre d'outils de la modale.
 */
export function TerritoryStageProvider({ territory = null, hosted = false, children }) {
  const viewerRef = useRef(null);

  // Ce qui est CONSTRUIT (territoire + couches), indépendant de qui regarde.
  const [scene, setScene] = useState(null);
  // La dernière vue déclarée. CONSERVÉE quand l'écran est relâché : une page
  // qui revient doit retrouver la carte là où elle l'a laissée, pas un écran
  // vide qu'il faudrait recadrer.
  const [stageView, setStageView] = useState(null);
  const [owner, setOwner] = useState(null);

  // Réglage de SÉANCE, et c'est tout l'intérêt de le poser ici : il vivait dans
  // la page d'intro, donc il retombait à 1 dès qu'on la quittait, alors qu'il
  // décrit la salle et le vidéoprojecteur — quelque chose qu'on règle une fois
  // en arrivant.
  const [uiScale, setUiScale] = useState(1);
  const [selection, setSelection] = useState(null);
  const handleSelectionChange = useCallback((next) => setSelection(next), []);

  /**
   * PRÉCHARGEMENT DÉCLARÉ PAR L'ATELIER (`games_config.territory`, recopié dans
   * la partie à l'initialisation).
   *
   * Deux services, et le second est le plus important :
   *
   *  1. la scène se construit dès le début de la séance, sans attendre la
   *     première page qui montre une carte — sans quoi un atelier qui ouvre sur
   *     une mosaïque en fond de carte afficherait un fond vide le temps du
   *     chargement ;
   *  2. l'UNION des couches de l'atelier est connue d'emblée. C'est ce qui
   *     évite qu'une page tardive en réclame une nouvelle et fasse reconstruire
   *     la scène en pleine séance (cf. `reconcileScene`).
   *
   * Sans cette déclaration, tout continue de fonctionner : la première page qui
   * réclame une carte la fait monter. On perd juste la garantie.
   */
  const territoryKey = territory?.id
    ? `${territory.id}|${[...new Set(territory.layers || [])].sort().join(",")}`
    : null;
  useEffect(() => {
    if (!territory?.id) return;
    setScene((current) => reconcileScene(current, {
      territory_id: territory.id,
      layers: territory.layers || [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryKey]);

  const commands = useMemo(() => ({
    claim: (ownerId) => setOwner(ownerId),
    // Ne relâche que si l'appelant est encore le propriétaire : entre deux
    // pages, le démontage de l'ancienne peut suivre le montage de la nouvelle.
    release: (ownerId) => setOwner((current) => (current === ownerId ? null : current)),
    setView: (ownerId, next) => {
      setScene((current) => reconcileScene(current, next));
      setStageView(next);
    },
    setUiScale,
    viewer: viewerRef,
  }), []);

  const state = useMemo(
    () => ({ selection, uiScale, hasStage: true }),
    [selection, uiScale]
  );

  // `off` = personne ne revendique l'écran. La scène reste montée et chargée,
  // seul le rendu est gelé (cf. `paused`) : c'est exactement ce qu'on cherchait
  // à obtenir, une carte qui attend au lieu de se reconstruire.
  const mode = owner ? (stageView?.mode || "stage") : "off";
  const modeSpec = MODES[mode] || MODES.stage;

  // Rotation : en fond de page elle est décidée par l'auteur et par défaut
  // NULLE — une carte qui tourne derrière une mosaïque est un choix, pas un
  // réglage par défaut, et c'est elle qui coûte des images par seconde. Au
  // premier plan, l'interrupteur de l'animateur reprend la main (`null`).
  const orbit = stageView?.motion?.orbit;
  const autoRotate = mode === "backdrop" ? (orbit ?? 0) : (orbit ?? null);

  const treatment = (mode === "backdrop" && stageView?.treatment) || {};

  /**
   * FOND DE CARTE : LES PAGES DE JEU CESSENT DE PEINDRE LE LEUR.
   *
   * `.page-general-container` porte un aplat opaque (`#05122b`) et deux calques
   * de fond ; tant qu'ils sont là, la carte est derrière un mur et le mode
   * `backdrop` ne montre rien.
   *
   * Un attribut sur `<body>` plutôt qu'une classe passée à chaque page : le
   * même aplat est peint par la mosaïque, le choix unique, le tableau de bord
   * et la page de contexte — quatre composants sans lien qui devraient tous
   * apprendre l'existence de la carte pour une règle qui n'est pas la leur.
   * L'attribut dit la seule chose qui compte, une fois : pendant que la carte
   * est en fond, aucune page ne pose son propre décor.
   */
  useEffect(() => {
    if (mode !== "backdrop") return undefined;
    document.body.dataset.territoryBackdrop = "true";
    return () => { delete document.body.dataset.territoryBackdrop; };
  }, [mode]);

  const layer = scene ? (
    <div
      className={`territory-stage${hosted ? " territory-stage--hosted" : ""}`}
      data-mode={mode}
      aria-hidden={modeSpec.chrome ? undefined : "true"}
      style={{
        "--t3d-backdrop-dim": treatment.dim ?? 0,
        "--t3d-backdrop-blur": `${treatment.blur ?? 0}px`,
        "--t3d-backdrop-saturate": treatment.saturate ?? 1,
      }}
    >
      <TerrainViewer
        ref={viewerRef}
        territoryId={scene.territoryId}
        availableLayers={scene.layers}
        activeLayers={stageView?.active_layers || []}
        layerSettings={stageView?.layer_settings || {}}
        cameraPose={stageView?.camera || null}
        fogEnabled={stageView?.fog_enabled !== false}
        points={stageView?.points || []}
        contentInsets={stageView?.content_insets || null}
        onSelectionChange={handleSelectionChange}
        uiScale={uiScale}
        paused={mode === "off"}
        chrome={modeSpec.chrome}
        maxFps={modeSpec.maxFps}
        autoRotate={autoRotate}
        openPointId={stageView?.open_point_id || null}
      />
      {/* Voile d'atténuation. Il est POSÉ SUR la carte plutôt qu'appliqué au
          canvas : un `filter` sur le conteneur du rendu WebGL force le
          navigateur à repasser chaque image dans un compositeur, ce qui coûte
          précisément là où on cherchait à économiser. Un aplat translucide ne
          coûte rien. Le flou, lui, n'a pas d'équivalent gratuit : il reste un
          filtre, et c'est à l'auteur de juger s'il le vaut. */}
      {mode === "backdrop" && <div className="territory-stage-veil" aria-hidden="true" />}
    </div>
  ) : null;

  return (
    <TerritoryStageContext.Provider value={commands}>
      <TerritoryStageStateContext.Provider value={state}>
        {/* L'écran AVANT les enfants : sans z-index de part et d'autre, l'ordre
            du DOM suffit à le faire passer dessous. */}
        {layer}
        {children}
      </TerritoryStageStateContext.Provider>
    </TerritoryStageContext.Provider>
  );
}

export default TerritoryStageProvider;
